import { CrosstabElementController } from '@sas-dvr/ltjs-bird';
import { CrosstabState } from '@sas-dvr/internal-va-react-core/store/birdElement/crosstabState';
import { VisualElementControllerLoader } from './LoaderFactory';
import { map, filter, distinctUntilChanged } from 'rxjs/operators';
import { MouseUpEventArgs } from '@sas-dvr/ltjs-commons/gestures/GestureTypes';
import {
  VisualizationManager,
  CrosstabPickResultEntry,
  CrosstabPickButtonType,
  PickEntryType,
} from '@sas-dvr/ltjs-commons';
import { filterNulls } from '../util';
import { toggleSortDirection } from '../util/sortUtils';
import { filterEntriesByType } from '../util/pickUtils';
import { loadBreadcrumb } from './loadBreadcrumb';
import { merge } from 'rxjs';
import { getMaximizeObservables } from '../util/maximizeUtils';
import { loadVisualization } from './loadVisualization';

export const loadCrosstabElementController: VisualElementControllerLoader = (
  visualController,
  visualElementController,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(visualElementController instanceof CrosstabElementController)) {
    throw new Error('Expected the controller to be a CrosstabElementController');
  }

  const visualId = deps.getKey(visualElementController);
  const contentId$ = state$.pipe(
    map((aState) => aState.birdElements.Crosstab[visualId]),
    filter(
      (myState): myState is Exclude<Required<typeof myState>, undefined> =>
        myState !== undefined && myState.contentId !== undefined
    ),
    distinctUntilChanged(),
    map((myState) => myState.contentId)
  );

  const visualizationManager = new VisualizationManager();
  const loadVisualElement$ = loadVisualization(
    visualController,
    visualElementController,
    unload$,
    myActions$,
    state$,
    deps,
    {
      create: () => CrosstabState.actions.add(visualId, {}),
      delete: () => CrosstabState.actions.delete(visualId),
    },
    { contentId$, visualizationManager }
  );

  visualizationManager.addGestureOverride('mouseUp', (gesture: MouseUpEventArgs) => {
    onMouseUp(visualElementController, visualizationManager, gesture);
  });

  const loadVisualBreadcrumb$ = loadBreadcrumb(
    visualElementController,
    unload$,
    myActions$,
    state$,
    visualId
  );

  const handleMaximize$ = getMaximizeObservables(
    visualId,
    visualController,
    visualElementController,
    state$,
    unload$,
    {
      preferOverflowMenu: true,
    }
  );

  return merge(loadVisualElement$, loadVisualBreadcrumb$, handleMaximize$);
};

function onMouseUp(
  controller: CrosstabElementController,
  visualizationManager: VisualizationManager,
  gesture: MouseUpEventArgs
) {
  if (gesture.localX === undefined || gesture.localY === undefined) {
    return;
  }

  const pickResults = visualizationManager.getPickResults(
    gesture.localX,
    gesture.localY,
    false,
    false
  );
  const entry = filterEntriesByType(pickResults, CrosstabPickResultEntry)[0];

  if (entry) {
    const buttonType = entry.getButtonType();
    if (buttonType === CrosstabPickButtonType.NONE) {
      const entryType = entry.getType();

      if (
        (entryType === PickEntryType.CROSSTAB_COLUMN_AXIS_LABEL_HEADER ||
          entryType === PickEntryType.CROSSTAB_ROW_AXIS_LABEL_HEADER ||
          entryType === PickEntryType.CROSSTAB_MEASURE_HEADER) &&
        controller.canSort(entry)
      ) {
        // Sort
        const sortController = controller.getTooltipSortControllerWithAllSortItems(pickResults);
        if (sortController) {
          // Get sort item. There should be only one sortItem if a column header was clicked.
          const sortItem = filterNulls(sortController.getSortItems())[0];
          if (sortItem) {
            toggleSortDirection(sortController, sortItem, true);
          }
        }
      }
    } else {
      const drillController = getTooltipDrillController(controller, entry);
      if (drillController) {
        switch (buttonType) {
          case CrosstabPickButtonType.CROSSTAB_DRILLDOWN_BUTTON:
            drillController.drillDown(entry.getNode());
            break;
          case CrosstabPickButtonType.CROSSTAB_EXPAND_BUTTON:
            drillController.expand(entry.getNode());
            break;
          case CrosstabPickButtonType.CROSSTAB_COLLAPSE_BUTTON:
            drillController.collapse(entry.getNode());
            break;
        }
      }
    }
  }
}

function getTooltipDrillController(
  controller: CrosstabElementController,
  entry: CrosstabPickResultEntry
) {
  const resultControllers = controller.getResultControllers();
  if (resultControllers) {
    const resultController = resultControllers[0];
    if (resultController) {
      const dataSet = resultController.getDataSet();
      if (dataSet) {
        return controller.getTooltipDrillController(entry.getHierarchyName(), dataSet.getName());
      }
    }
  }
  return null;
}
